import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ScannedFoods = () => {
  console.log('[ScannedFoods] Component rendering');
  console.log('[ScannedFoods] window.location:', {
    origin: window.location.origin,
    pathname: window.location.pathname,
    href: window.location.href
  });
  console.log('[ScannedFoods] API URL base:', process.env.REACT_APP_API_URL || 'using proxy');
  
  const { fetchWithAuth } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('1');
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  
  console.log('[ScannedFoods] Current state:', {
    usersCount: users.length,
    loading,
    error,
    dateRange,
    expandedUsersCount: expandedUsers.size
  });

  useEffect(() => {
    console.log('[ScannedFoods] Component mounted/updated, dateRange:', dateRange);
    fetchScannedFoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchScannedFoods = async () => {
    console.log('[ScannedFoods] Starting to fetch scanned foods, dateRange:', dateRange);
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = `/api/foods/scanned-users?days=${dateRange}`;
      console.log('[ScannedFoods] Fetching from endpoint:', endpoint);
      console.log('[ScannedFoods] Full URL:', window.location.origin + endpoint);
      
      const response = await fetchWithAuth(endpoint);
      console.log('[ScannedFoods] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ScannedFoods] Response not OK:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Failed to fetch scanned foods: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[ScannedFoods] Data received:', {
        usersCount: data.users?.length,
        total: data.total,
        dateRange: data.dateRange,
        firstUser: data.users?.[0]
      });
      console.log('[ScannedFoods] Full data structure:', data);
      
      setUsers(data.users || []);
      
      // Auto-expand all users by default
      const allUserIds = new Set((data.users || []).map(u => u.user_id));
      console.log('[ScannedFoods] Auto-expanding all users:', Array.from(allUserIds));
      setExpandedUsers(allUserIds);
      
      console.log('[ScannedFoods] State updated successfully');
    } catch (err) {
      console.error('[ScannedFoods] Error occurred:', {
        message: err.message,
        stack: err.stack,
        error: err
      });
      setError(err.message);
    } finally {
      setLoading(false);
      console.log('[ScannedFoods] Fetch completed, loading set to false');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleUser = (userId) => {
    console.log('[ScannedFoods] Toggling user:', userId);
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      console.log('[ScannedFoods] Collapsing user:', userId);
      newExpanded.delete(userId);
    } else {
      console.log('[ScannedFoods] Expanding user:', userId);
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const expandAll = () => {
    console.log('[ScannedFoods] Expanding all users');
    setExpandedUsers(new Set(users.map(u => u.user_id)));
  };

  const collapseAll = () => {
    console.log('[ScannedFoods] Collapsing all users');
    setExpandedUsers(new Set());
  };
  
  const handleDateRangeChange = (e) => {
    const newValue = e.target.value;
    console.log('[ScannedFoods] Date range changed:', {
      oldValue: dateRange,
      newValue: newValue
    });
    setDateRange(newValue);
  };

  if (loading && users.length === 0) {
    console.log('[ScannedFoods] Rendering loading state');
    return <div className="loading">Loading...</div>;
  }
  
  if (error) {
    console.error('[ScannedFoods] Rendering error state:', error);
    return (
      <div className="error">
        <h2>Error</h2>
        <p>{error}</p>
        <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
          Check browser console for detailed logs
        </p>
      </div>
    );
  }
  
  console.log('[ScannedFoods] Rendering main UI with', users.length, 'users');

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>
        Scanned Foods by User
      </h1>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '8px', fontWeight: '600' }}>Date Range:</label>
            <select 
              value={dateRange} 
              onChange={handleDateRangeChange}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="1">Last 24 hours</option>
              <option value="2">Last 2 days</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button 
              onClick={expandAll}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Expand All
            </button>
            <button 
              onClick={collapseAll}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ marginBottom: '16px', color: '#666' }}>
          {users.length} users with food logs
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {users.map((user) => (
            <div 
              key={user.user_id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              {/* User Header */}
              <div 
                onClick={() => toggleUser(user.user_id)}
                style={{
                  padding: '16px',
                  background: expandedUsers.has(user.user_id) ? '#f9fafb' : '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background 0.2s',
                  ':hover': { background: '#f9fafb' }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: expandedUsers.has(user.user_id) ? 'rotate(90deg)' : 'rotate(0)' }}>
                    ▶
                  </span>
                  <div>
                    <Link 
                      to={`/users/${user.user_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="link"
                      style={{ fontSize: '18px', fontWeight: '600' }}
                    >
                      {user.user_name || user.user_email || `User ${user.user_id}`}
                    </Link>
                    {user.user_email && user.user_name && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        {user.user_email}
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {user.food_count} food{user.food_count !== 1 ? 's' : ''}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                      Last: {formatDate(user.last_food_date)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Foods List - Horizontal Scroll */}
              {expandedUsers.has(user.user_id) && (
                <div style={{ 
                  padding: '16px', 
                  background: '#fafafa',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  {user.foods && user.foods.length > 0 ? (
                    <div style={{ 
                      display: 'flex', 
                      gap: '16px',
                      overflowX: 'auto',
                      paddingBottom: '8px',
                      scrollbarWidth: 'thin'
                    }}>
                      {user.foods.map((food, index) => (
                        <div 
                          key={food.id}
                          style={{
                            minWidth: '280px',
                            maxWidth: '280px',
                            background: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}
                        >
                          {/* Food Image */}
                          {food.image_url ? (
                            <div style={{
                              width: '100%',
                              height: '200px',
                              background: '#f0f0f0',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <img 
                                src={food.image_url}
                                alt={food.name || 'Food'}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 48px;">🍽️</div>';
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                background: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                #{index + 1}
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '200px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '64px',
                              position: 'relative'
                            }}>
                              🍽️
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                background: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                #{index + 1}
                              </div>
                            </div>
                          )}

                          {/* Food Details */}
                          <div style={{ padding: '12px' }}>
                            <div style={{ 
                              fontSize: '16px',
                              fontWeight: '600',
                              marginBottom: '8px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {food.name || 'Unnamed Food'}
                            </div>
                            
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: '6px',
                              fontSize: '13px',
                              color: '#666',
                              marginBottom: '8px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>🔥 Calories</span>
                                <strong>{Math.round(food.calories || 0)} kcal</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>🥩 Protein</span>
                                <strong>{Math.round(food.protein || 0)}g</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>🍞 Carbs</span>
                                <strong>{Math.round(food.carbs || 0)}g</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>🥑 Fats</span>
                                <strong>{Math.round(food.fats || 0)}g</strong>
                              </div>
                            </div>

                            {food.serving && (
                              <div style={{ 
                                fontSize: '12px',
                                color: '#888',
                                marginBottom: '8px',
                                paddingTop: '8px',
                                borderTop: '1px solid #f0f0f0'
                              }}>
                                Serving: {food.serving} {food.serving_unit || ''}
                              </div>
                            )}

                            <div style={{ 
                              fontSize: '11px', 
                              color: '#999',
                              paddingTop: '8px',
                              borderTop: '1px solid #f0f0f0'
                            }}>
                              {formatDateTime(food.created_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      No foods found
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {users.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#999' 
          }}>
            No users with food logs found in this date range
          </div>
        )}
      </div>
    </div>
  );
};

export default ScannedFoods;
